import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Divider, Grid, CircularProgress, IconButton, Tooltip, InputAdornment } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, People as PeopleIcon, Search as SearchIcon, ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import MainLayout from '../layouts/MainLayout';
import { colaboradorService, Colaborador, CreateColaboradorDTO } from '../services/colaboradorService';
import { formatarCPF, formatarCEP } from '../utils/formatters';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useForm, Controller } from 'react-hook-form';

const estadosBrasileiros = [
  { uf: 'AC', nome: 'ACRE' },
  { uf: 'AL', nome: 'ALAGOAS' },
  { uf: 'AP', nome: 'AMAPÁ' },
  { uf: 'AM', nome: 'AMAZONAS' },
  { uf: 'BA', nome: 'BAHIA' },
  { uf: 'CE', nome: 'CEARÁ' },
  { uf: 'DF', nome: 'DISTRITO FEDERAL' },
  { uf: 'ES', nome: 'ESPÍRITO SANTO' },
  { uf: 'GO', nome: 'GOIÁS' },
  { uf: 'MA', nome: 'MARANHÃO' },
  { uf: 'MT', nome: 'MATO GROSSO' },
  { uf: 'MS', nome: 'MATO GROSSO DO SUL' },
  { uf: 'MG', nome: 'MINAS GERAIS' },
  { uf: 'PA', nome: 'PARÁ' },
  { uf: 'PB', nome: 'PARAÍBA' },
  { uf: 'PR', nome: 'PARANÁ' },
  { uf: 'PE', nome: 'PERNAMBUCO' },
  { uf: 'PI', nome: 'PIAUÍ' },
  { uf: 'RJ', nome: 'RIO DE JANEIRO' },
  { uf: 'RN', nome: 'RIO GRANDE DO NORTE' },
  { uf: 'RS', nome: 'RIO GRANDE DO SUL' },
  { uf: 'RO', nome: 'RONDÔNIA' },
  { uf: 'RR', nome: 'RORAIMA' },
  { uf: 'SC', nome: 'SANTA CATARINA' },
  { uf: 'SP', nome: 'SÃO PAULO' },
  { uf: 'SE', nome: 'SERGIPE' },
  { uf: 'TO', nome: 'TOCANTINS' },
];

const GerenciarColaboradoresPage = () => {
  const [colaboradores, setColaboradores] = useState<Colaborador[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [colaboradorAtual, setColaboradorAtual] = useState<Colaborador | null>(null);
  const [loadingCep, setLoadingCep] = useState(false);

  const {
    register,
    handleSubmit: handleSubmitForm,
    control,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<CreateColaboradorDTO>({
    defaultValues: {
      nome: '',
      cpf: '',
      rg: '',      
      dataNascimento: '',
      cargo: '',
      dataAdmissao: '',
      banco: '',
      agencia: '',
      conta: '',
      pix: '',
      cep: '',
      endereco: '',
      numero: '',
      complemento: '',
      bairro: '',
      cidade: '',
      estado: '',
    },
  });

  const carregarColaboradores = async () => {
    setLoading(true);
    try {
      const data = await colaboradorService.listar();
      setColaboradores(data);
    } catch (error) {
      console.error('Erro ao carregar colaboradores:', error);
      toast.error('Erro ao carregar colaboradores. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarColaboradores();
  }, []);

  const formatarData = (data: string | Date| null) => {
    if (!data) return '';
    const date = new Date(data);
    return date.toISOString().split('T')[0];
  };

  const handleOpenDialog = (colaborador?: Colaborador) => {
    if (colaborador) {
      setColaboradorAtual(colaborador);
      reset({
        nome: colaborador.nome,
        cpf: colaborador.cpf,
        rg: colaborador.rg || '',
        dataNascimento: formatarData(colaborador.dataNascimento),
        cargo: colaborador.cargo || '',
        dataAdmissao: formatarData(colaborador.dataAdmissao),
        banco: colaborador.banco,
        agencia: colaborador.agencia,
        conta: colaborador.conta,
        pix: colaborador.pix || '',
        cep: colaborador.endereco.cep,
        endereco: colaborador.endereco.endereco,
        numero: colaborador.endereco.numero,
        complemento: colaborador.endereco.complemento || '',
        bairro: colaborador.endereco.bairro,
        cidade: colaborador.endereco.cidade,
        estado: colaborador.endereco.estado,
      });
    } else {
      setColaboradorAtual(null);
      reset({
        nome: '',
        cpf: '',
        dataNascimento: '',
        cargo: '',
        dataAdmissao: '',
        banco: '',
        agencia: '',
        conta: '',
        pix: '',
        cep: '',
        endereco: '',
        numero: '',
        complemento: '',
        bairro: '',
        cidade: '',
        estado: '',
      });
    }
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setColaboradorAtual(null);
  };

  const onSubmit = async (formData: CreateColaboradorDTO) => {
    try {
      const dadosParaEnviar = { 
        ...formData,
        dataNascimento: formData.dataNascimento || null,
        dataAdmissao: formData.dataAdmissao || null,
      };
     
      if (colaboradorAtual) {
        await colaboradorService.atualizar(colaboradorAtual.id, dadosParaEnviar);
        toast.success('Colaborador atualizado com sucesso!');
      } else {
        await colaboradorService.criar(dadosParaEnviar);
        toast.success('Colaborador criado com sucesso!');
      }
      handleCloseDialog();
      carregarColaboradores();
    } catch (error) {
      console.error('Erro ao salvar colaborador:', error);
      toast.error('Erro ao salvar colaborador. Por favor, tente novamente.');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Tem certeza que deseja excluir este colaborador?')) {
      try {
        await colaboradorService.excluir(id);
        toast.success('Colaborador excluído com sucesso!');
        carregarColaboradores();
      } catch (error) {
        console.error('Erro ao excluir colaborador:', error);
        toast.error('Erro ao excluir colaborador. Por favor, tente novamente.');
      }
    }
  };

  const handleBuscaCep = async () => {
    const cep = getValues('cep')?.replace(/\D/g, ''); // Remove caracteres não numéricos
    if (cep && cep.length === 8) {
      setLoadingCep(true);
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
        const data = await response.json();
        if (data.erro) {
          toast.error('CEP não encontrado.');
        } else {
          setValue('endereco', data.logradouro.toUpperCase(), { shouldValidate: true });
          setValue('bairro', data.bairro.toUpperCase(), { shouldValidate: true });
          setValue('cidade', data.localidade.toUpperCase(), { shouldValidate: true });
          setValue('estado', data.uf.toUpperCase(), { shouldValidate: true });
          toast.success('Endereço preenchido automaticamente!');
        }
      } catch (error) {
        console.error('Erro ao buscar CEP:', error);
        toast.error('Erro ao buscar CEP. Tente novamente.');
      } finally {
        setLoadingCep(false);
      }
    } else {
      toast.warn('Por favor, insira um CEP válido com 8 dígitos.');
    }
  };

  const handleCopyToClipboard = () => {
    const pixValue = getValues('pix');
    if (!pixValue) {
      toast.warn('O campo PIX está vazio.');
      return;
    }
    navigator.clipboard.writeText(pixValue).then(() => {
      toast.success('Chave PIX copiada para a área de transferência!');
    }).catch(err => {
      console.error('Erro ao copiar a chave PIX: ', err);
      toast.error('Não foi possível copiar a chave PIX.');
    });
  };
  return (
    <MainLayout>
      <Box sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h4" component="h1" color="primary.main" sx={{ fontWeight: 'bold' }}>
            <PeopleIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: '1.2em' }} />
            Gerenciamento de Colaboradores
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
            Novo Colaborador
          </Button>
        </Box>
        
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 'bold', textTransform: 'uppercase', bgcolor: 'grey.100' } }}>
                  <TableCell>Nome</TableCell>
                  <TableCell>CPF</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {colaboradores.map(colab => (
                  <TableRow key={colab.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell>{colab.nome}</TableCell>
                    <TableCell>{formatarCPF(colab.cpf)}</TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                        <Tooltip title="Editar">
                          <IconButton color="primary" onClick={() => handleOpenDialog(colab)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton color="error" onClick={() => handleDelete(colab.id)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      <Dialog open={open} onClose={handleCloseDialog} maxWidth="md" fullWidth component="form" onSubmit={handleSubmitForm(onSubmit)}>
        <DialogTitle>
          {colaboradorAtual ? 'Editar Colaborador' : 'Novo Colaborador'}
        </DialogTitle>
        <DialogContent dividers >
          {/* Sessão: Dados Pessoais */}
          <Typography variant="h6" gutterBottom>Dados Pessoais</Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2} mb={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Nome Completo"
                fullWidth
                {...register('nome', { required: 'Nome é obrigatório' })}
                error={!!errors.nome}
                onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                  e.target.value = e.target.value.toUpperCase();
                }}
                helperText={errors.nome?.message}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="cpf"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="CPF"
                    fullWidth
                    onChange={(e) => field.onChange(formatarCPF(e.target.value))}
                    inputProps={{
                      maxLength: 14 // Limita o campo a 11 números + 3 caracteres de formatação (..-)
                    }}
                    error={!!errors.cpf}
                    helperText={errors.cpf?.message}
                  />
                )}
              />

            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="RG"
                fullWidth
                {...register('rg')}
                onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                  e.target.value = e.target.value.toUpperCase();
                }}
                error={!!errors.rg}
                helperText={errors.rg?.message}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Data de Nascimento"
                type="date"
                InputLabelProps={{ shrink: true }}
                fullWidth
                {...register('dataNascimento')}
                error={!!errors.dataNascimento}
                helperText={errors.dataNascimento?.message}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Cargo"
                fullWidth
                {...register('cargo')}
                error={!!errors.cargo}
                onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                  e.target.value = e.target.value.toUpperCase();
                }}
                helperText={errors.cargo?.message}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Data de Admissão"
                type="date"
                InputLabelProps={{ shrink: true }}
                fullWidth
                {...register('dataAdmissao')}
                error={!!errors.dataAdmissao}
                helperText={errors.dataAdmissao?.message}
              />
            </Grid>
          </Grid>

          {/* Sessão: Dados Bancários */}
          <Typography variant="h6" gutterBottom>Dados Bancários</Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2} mb={3}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Banco"
                fullWidth
                {...register('banco')}
                onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                  e.target.value = e.target.value.toUpperCase();
                }}
                error={!!errors.banco}
                helperText={errors.banco?.message}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Agência"
                fullWidth
                {...register('agencia')}
                onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                  e.target.value = e.target.value.toUpperCase();
                }}
                error={!!errors.agencia}
                helperText={errors.agencia?.message}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Conta"
                fullWidth
                {...register('conta')}
                onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                  e.target.value = e.target.value.toUpperCase();
                }}
                error={!!errors.conta}
                helperText={errors.conta?.message}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 12 }}>
              <TextField
                label="Chave PIX"
                fullWidth
                {...register('pix')}
                helperText={errors.pix?.message}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Copiar Chave PIX">
                        <IconButton onClick={handleCopyToClipboard} edge="end">
                          <ContentCopyIcon />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>

          {/* Sessão: Endereço */}
          <Typography variant="h6" gutterBottom>Endereço</Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2} mb={3}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Controller
                name="cep"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="CEP"
                    fullWidth
                    onChange={(e) => field.onChange(formatarCEP(e.target.value))}
                    error={!!errors.cep}
                    helperText={errors.cep?.message}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title="Buscar Endereço por CEP">
                            <IconButton onClick={handleBuscaCep} edge="end" disabled={loadingCep}>
                              {loadingCep ? <CircularProgress size={24} /> : <SearchIcon />}
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <Controller
                name="endereco"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Endereço"
                    fullWidth
                    error={!!errors.endereco}
                    onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                      e.target.value = e.target.value.toUpperCase();
                    }}
                    helperText={errors.endereco?.message}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Número"
                fullWidth
                {...register('numero')}
                onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                  e.target.value = e.target.value.toUpperCase();
                }}
                error={!!errors.numero}
                helperText={errors.numero?.message}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <Controller
                name="complemento"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Complemento" fullWidth
                    onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                      e.target.value = e.target.value.toUpperCase();
                    }} />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Controller
                name="bairro"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Bairro"
                    fullWidth
                    error={!!errors.bairro}
                    onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                      e.target.value = e.target.value.toUpperCase();
                    }}
                    helperText={errors.bairro?.message}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Controller
                name="cidade"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Cidade"
                    fullWidth
                    error={!!errors.cidade}
                    onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                      e.target.value = e.target.value.toUpperCase();
                    }}
                    helperText={errors.cidade?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button type="submit" variant="contained">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </MainLayout>
  );
};

export default GerenciarColaboradoresPage; 